import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Rocket, Heart, Trophy, Shield, Clock, Users, Target } from 'lucide-react';

const WhyGroviyo = () => {
  const mainRef = useRef(null);
  const sectionsRef = useRef(null);
  
  const isMainInView = useInView(mainRef, { once: true, margin: "-100px" });
  const isSectionsInView = useInView(sectionsRef, { once: true, margin: "-100px" });

  const benefits = [
    { icon: Star, title: "Digital Growth Experts" },
    { icon: Trophy, title: "Results-Driven" },
    { icon: Target, title: "Full-Funnel Strategy" },
    { icon: Users, title: "Professional Team" }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    })
  };

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Enhanced animated background */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-80 h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            y: [0, -20, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 w-60 h-60 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-20"
          animate={{ 
            scale: [1.2, 1, 1.2],
            x: [-50, 50, -50],
            y: [-30, 30, -30]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
            y: [0, 20, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8">
            <span className="bg-[#f4b400] bg-clip-text text-transparent inline-block p-2 leading-relaxed bg-[length:100%_120%] bg-bottom">
              Why Groviyo?
            </span>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex items-center justify-center gap-2 text-gray-600 text-sm"
          >
            <Clock className="w-4 h-4" />
            <span>Updated: September 22, 2024</span>
          </motion.div>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.2 }
            }
          }}
          initial="hidden"
          animate="visible"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              variants={cardVariants}
              custom={index}
              className="p-6 bg-white/80 backdrop-blur-sm rounded-xl text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <benefit.icon className="w-8 h-8 mx-auto mb-3 text-[#ffd54f]" />
              <h3 className="font-semibold text-gray-800">{benefit.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <motion.div
          ref={mainRef}
          className="max-w-4xl mx-auto space-y-12"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isMainInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
          >
            <p className="text-lg leading-relaxed mb-6">
              We're not your typical agency. We're the digital growth experts that brands trust to deliver results with creative campaigns, full-funnel strategies, and data-driven optimization. We are a team of web development professionals, SEO geeks, story telling "edu-tainment" style email markers, and paid advertising wizards. At your service to help your business grow and thrive online.
            </p>
            <p className="text-lg leading-relaxed">
              At <span className="font-bold text-[#f4b400]">Groviyo</span>, we eliminate the fear of getting it wrong. No more guessing, no more sleepless nights worrying if your business is growing the way it should. We provide peace of mind with proven strategies that help your brand succeed online—so you can focus on what matters most.
            </p>
          </motion.div>

          <div ref={sectionsRef} className="space-y-8">
            {/* First Impressions Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isSectionsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start gap-4">
                <Heart className="w-8 h-8 text-[#ffd54f] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">First Impressions Matter</h2>
                  <p className="text-gray-700">Just like in life, first impressions matter. At <span className="font-bold text-[#f4b400]">Groviyo</span>, we make sure your brand stands out and stays in the minds (and inboxes) of your customers. Our approach builds relationships that last—turning casual visitors into loyal, returning customers who keep coming back for more.</p>
                </div>
              </div>
            </motion.div>

            {/* Competition Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isSectionsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start gap-4">
                <Rocket className="w-8 h-8 text-[#ffd54f] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">Stay Ahead of Competition</h2>
                  <p className="text-gray-700">In today's competitive world, staying ahead of the competition is key. <span className="font-bold text-[#f4b400]">Groviyo</span> helps you dominate the digital landscape with strategies that ensure your brand rises to the top. We'll help you outshine competitors, increase your sales, and keep your business winning—because second place just isn't an option.</p>
                </div>
              </div>
            </motion.div>

            {/* Future Protection Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isSectionsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-[#ffd54f] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">Protect Your Future</h2>
                  <p className="text-gray-700">Our online growth solutions are designed to protect your business's future, ensuring it provides for you and the people who matter most. We craft strategies that not only get you noticed but make your customers proud to be associated with you. We don't just help you grow—we help you gain the recognition your business deserves.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhyGroviyo;